import { z } from 'zod';
import { logger } from '@/utils/logger';

/**
 * Autonomous Model Configuration
 *
 * CRITICAL: This file defines the ONLY approved Autonomous models for this application.
 * See MODEL_POLICY.md for the full policy.
 */

// Approved model categories and IDs
export const APPROVED_MODELS = {
    TEXT_AGENT: 'gemini-3.1-pro-preview',        // Complex reasoning — per MODEL_POLICY.md
    TEXT_FAST: 'gemini-3.8-flash',               // Workhorse agentic & fast tasks (GA) — per MODEL_POLICY.md
    TEXT_FLASH_3_8: 'gemini-3.8-flash',          // Gemini 3.8 Flash GA (September 2026)
    TEXT_FLASH_PREVIEW: 'gemini-3-flash-preview', // Legacy fast preview reference
    TEXT_LITE: 'gemini-3.1-flash-lite',           // Budget tier — per MODEL_POLICY.md
    IMAGE_GEN: 'gemini-3-pro-image',              // Native image gen via responseModalities
    IMAGE_FAST: 'gemini-3.1-flash-image',         // Nano Banana 2 — fast image gen
    // Direct mode — GA models for client-side SDK calls
    DIRECT_PRO: 'gemini-3-pro-image',             // Nano Banana Pro — highest quality, 4K, 14 ref images
    DIRECT_FAST: 'gemini-3.1-flash-image',        // Nano Banana 2 — fast + Pro quality
    // Imagen 4 specifically for backwards compatibility and fallback options
    IMAGEN_ULTRA: 'imagen-4.0-ultra-generate-001',
    IMAGEN_PRO: 'imagen-4.0-generate-001',
    IMAGEN_FAST: 'imagen-4.0-fast-generate-001',
    AUDIO_PRO: 'gemini-3.1-pro-preview',
    AUDIO_FLASH: 'gemini-3.8-flash',
    AUDIO_TTS: 'gemini-3.1-flash-tts-preview',
    VIDEO_PRO: 'veo-3.1-generate-001',
    VIDEO_FAST: 'veo-3.1-fast-generate-001',  // Fast mode — lower latency, lower cost
    VIDEO_LITE: 'veo-3.1-lite-generate-001',  // Lite mode — lowest cost, good quality
    VIDEO_GEN: 'veo-3.1-generate-001',        // Alias for backward compatibility
    VIDEO_OMNI: 'gemini-omni-1.1-flash',  // Gemini Omni Flash GA — multimodal video QA and continuity
    BROWSER_AGENT: 'gemini-3.1-pro-preview',
    EMBEDDING_DEFAULT: 'gemini-embedding-001'
} as const;

export const INTELLIGENCE_MODELS = {
    TEXT: {
        AGENT: APPROVED_MODELS.TEXT_AGENT,
        FAST: APPROVED_MODELS.TEXT_FAST,
        LITE: APPROVED_MODELS.TEXT_LITE,
        FLASH_3_8: APPROVED_MODELS.TEXT_FLASH_3_8,
    },
    IMAGE: {
        GENERATION: APPROVED_MODELS.IMAGE_GEN,
        FAST: APPROVED_MODELS.IMAGE_FAST,
        DIRECT_PRO: APPROVED_MODELS.DIRECT_PRO,
        DIRECT_FAST: APPROVED_MODELS.DIRECT_FAST,
        IMAGEN_ULTRA: APPROVED_MODELS.IMAGEN_ULTRA,
        IMAGEN_PRO: APPROVED_MODELS.IMAGEN_PRO,
        IMAGEN_FAST: APPROVED_MODELS.IMAGEN_FAST,
    },
    AUDIO: {
        PRO: APPROVED_MODELS.AUDIO_PRO,
        FLASH: APPROVED_MODELS.AUDIO_FLASH,
        TTS: APPROVED_MODELS.AUDIO_TTS,
    },
    VIDEO: {
        PRO: APPROVED_MODELS.VIDEO_PRO,
        FAST: APPROVED_MODELS.VIDEO_FAST,
        LITE: APPROVED_MODELS.VIDEO_LITE,
        OMNI: APPROVED_MODELS.VIDEO_OMNI,
        EDIT: APPROVED_MODELS.VIDEO_PRO,
        GENERATION: APPROVED_MODELS.VIDEO_PRO // Backward compatibility
    },
    BROWSER: {
        AGENT: APPROVED_MODELS.BROWSER_AGENT,
    },
    // CE-3, ISSUE-1112: reuses BROWSER_AGENT — same screenshot-reasoning loop shape
    // (capture -> reason -> act), just coordinate-space instead of CSS-selector-space.
    COMPUTER: {
        AGENT: APPROVED_MODELS.BROWSER_AGENT,
    },
    EMBEDDING: {
        DEFAULT: APPROVED_MODELS.EMBEDDING_DEFAULT,
    }
} as const;

// Zod schema for runtime validation
export const ModelIdSchema = z.enum(Object.values(APPROVED_MODELS) as [string, ...string[]]);

export const INTELLIGENCE_CONFIG = {
    THINKING: {
        HIGH: {
            thinkingConfig: { thinkingLevel: "HIGH" }
        },
        MEDIUM: {
            thinkingConfig: { thinkingLevel: "MEDIUM" }
        },
        LOW: {
            thinkingConfig: { thinkingLevel: "LOW" }
        }
    },
    TEXT: {
        /** Judgment layer: verbosity/cost backstop for agent-loop text responses.
         *  Per-agent override via AgentConfig.maxOutputTokens. */
        MAX_OUTPUT_TOKENS_AGENT: 8192,
    },
    MEDIA_RESOLUTION: {
        DEFAULT: 'MEDIA_RESOLUTION_HIGH',
        LOW: 'MEDIA_RESOLUTION_LOW'
    },
    IMAGE: {
        DEFAULT: {
            imageConfig: { imageSize: '4K' }, // Nano Banana Pro supports up to 4K
            mediaResolution: 'MEDIA_RESOLUTION_HIGH',
            maxReferenceImages: 14
        },
        FAST: {
            // ISSUE-320: Nano Banana 2 (Flash) supports up to 4K. This value MUST
            // mirror maxResolution for 'gemini-3.1-flash-image' in the canonical
            // registry (@indii/shared modelCapabilities) — drift fails CI via
            // capabilitySync.test.ts.
            imageConfig: { imageSize: '4K' },
            mediaResolution: 'MEDIA_RESOLUTION_LOW',
            maxReferenceImages: 8
        }
    },
    VIDEO: {
        MIN_TIMEOUT_MS: 120000,
        TIMEOUT_PER_SECOND_MS: 12000,
        MAX_TIMEOUT_MS: 600000,
        DEFAULT_DURATION_SECONDS: 8,
        RESOLUTIONS: {
            SD: '720p',
            HD: '1080p',
            UHD: '4k'
        }
    },
    EMBEDDING: {
        DIMENSIONS: 768, // MRL: supported values are 768, 1536, or 3072
    }
} as const;

/**
 * Model Pricing (USD per 1M tokens for text; per second for video)
 *
 * gemini-2.5-pro:   $1.25 input / $10.00 output per 1M tokens
 * gemini-2.5-flash: $0.15 input /  $0.60 output per 1M tokens
 */
export const MODEL_PRICING = {
    'gemini-2.5-pro': { input: 1.25, output: 10.00 },
    'gemini-2.5-flash': { input: 0.15, output: 0.60 },
    'gemini-3.1-pro-preview': { input: 1.25, output: 10.00 },
    'gemini-3.8-flash': { input: 0.15, output: 0.60 },
    'gemini-3-flash-preview': { input: 0.15, output: 0.60 },
    'gemini-3.1-flash-lite': { input: 0.04, output: 0.20 },
    'veo-3.1-generate-001': {
        perSecond: 0.20,     // 720p/1080p Video Only
        perSecond4K: 0.40,   // 4K Video Only
        audioAddOn: 0.20     // Flat add-on for audio (up to 1080p)
    },
    'veo-3.1-fast-generate-001': {
        perSecond: 0.10,     // 720p/1080p Video Only
        perSecond4K: 0.30,   // 4K Video Only
        audioAddOn: 0.05     // Flat add-on for audio
    },
    'veo-3.1-lite-generate-001': {
        perSecond: 0.05,     // 720p/1080p Video Only — lowest cost tier
        audioAddOn: 0.02     // Flat add-on for audio
    },
    'gemini-3.1-flash-tts-preview': { input: 1.00, output: 20.00 },
    // Direct mode image models (token-based pricing, same tier as text)
    'gemini-3-pro-image': { input: 1.25, output: 10.00 },
    'gemini-3.1-flash-image': { input: 0.15, output: 0.60 },
    // New Imagen 4 models pricing (matches equivalent quality tiers)
    'imagen-4.0-ultra-generate-001': { input: 1.25, output: 10.00 },
    'imagen-4.0-generate-001': { input: 0.15, output: 0.60 },
    'imagen-4.0-fast-generate-001': { input: 0.10, output: 0.40 },
} as const;

/**
 * Calculate video generation timeout based on duration
 */
export function calculateVideoTimeout(durationSeconds: number): number {
    const { MIN_TIMEOUT_MS, TIMEOUT_PER_SECOND_MS, MAX_TIMEOUT_MS } = INTELLIGENCE_CONFIG.VIDEO;
    const calculated = Math.max(MIN_TIMEOUT_MS, durationSeconds * TIMEOUT_PER_SECOND_MS);
    return Math.min(calculated, MAX_TIMEOUT_MS);
}

// ============================================================================
// RUNTIME VALIDATION
// ============================================================================

const FORBIDDEN_PATTERNS: RegExp[] = [
    /gemini-1\./i,            // Block all legacy 1.x models
    /gemini-2\.0/i,           // Block 2.0 models — allow 2.5.x (TTS only)
    /imagen(?!-4)/i,          // Block all older Imagen models (replaced by Nano Banana, permit only imagen-4.0-*)
    /gemini-2\.5-flash-image/i, // Block Nano Banana OG (legacy)
    /^gemini-3-pro-image-preview$/i,       // Block retired preview endpoint (retired July 17, 2026)
    /^gemini-3\.1-flash-image-preview$/i,  // Block retired preview endpoint (retired July 17, 2026)
];

function validateModels(): void {
    const allModels = Object.values(APPROVED_MODELS);

    for (const modelId of allModels) {
        // Zod validation
        const result = ModelIdSchema.safeParse(modelId);
        if (!result.success) {
            throw new Error(`INTERNAL ERROR: Model ${modelId} not in APPROVED_MODELS list.`);
        }

        // Pattern validation
        for (const pattern of FORBIDDEN_PATTERNS) {
            if (pattern.test(modelId)) {
                const errorMessage = `
╔══════════════════════════════════════════════════════════════════════════════╗
║                        FORBIDDEN MODEL DETECTED                               ║
╠══════════════════════════════════════════════════════════════════════════════╣
║  Model ID: ${modelId.padEnd(64)}║
║                                                                              ║
║  This model is BANNED by MODEL_POLICY.md.                                    ║
╚══════════════════════════════════════════════════════════════════════════════╝
`;
                logger.error(errorMessage);
                throw new Error(`FORBIDDEN MODEL: ${modelId}. See MODEL_POLICY.md for approved models.`);
            }
        }
    }
}

// Centralized mapping of Model IDs to display names in UI components to prevent hardcoding.
export const MODEL_DISPLAY_NAMES: Record<string, string> = {
    [APPROVED_MODELS.TEXT_FAST]: 'Gemini 3.8 Flash',
    [APPROVED_MODELS.DIRECT_PRO]: 'Nano Banana Pro',
    [APPROVED_MODELS.DIRECT_FAST]: 'Nano Banana 2',
    [APPROVED_MODELS.IMAGEN_ULTRA]: 'Imagen 4 Ultra',
    [APPROVED_MODELS.IMAGEN_PRO]: 'Imagen 4',
    [APPROVED_MODELS.IMAGEN_FAST]: 'Imagen 4 Fast',
    [APPROVED_MODELS.VIDEO_PRO]: 'Veo 3.1',
    [APPROVED_MODELS.VIDEO_FAST]: 'Veo 3.1 Fast',
    [APPROVED_MODELS.VIDEO_LITE]: 'Veo 3.1 Lite',
    [APPROVED_MODELS.VIDEO_OMNI]: 'Gemini Omni Flash',
} as const;

// Run validation on module load
validateModels();

// Export type helpers
export type TextModel = typeof INTELLIGENCE_MODELS.TEXT[keyof typeof INTELLIGENCE_MODELS.TEXT];
export type ImageModel = typeof INTELLIGENCE_MODELS.IMAGE[keyof typeof INTELLIGENCE_MODELS.IMAGE];
export type AudioModel = typeof INTELLIGENCE_MODELS.AUDIO[keyof typeof INTELLIGENCE_MODELS.AUDIO];
export type VideoModel = typeof INTELLIGENCE_MODELS.VIDEO[keyof typeof INTELLIGENCE_MODELS.VIDEO];
export type LiteModel = typeof INTELLIGENCE_MODELS.TEXT.LITE;
export type BrowserModel = typeof INTELLIGENCE_MODELS.BROWSER[keyof typeof INTELLIGENCE_MODELS.BROWSER];

/**
 * Reverse lookup to find the configuration key for a given model ID.
 * Used by RemoteConfig to find overrides.
 */
export function getModelKey(modelId: string): keyof typeof APPROVED_MODELS | undefined {
    const entry = Object.entries(APPROVED_MODELS).find(([_, value]) => value === modelId);
    return entry ? (entry[0] as keyof typeof APPROVED_MODELS) : undefined;
}
